import apiClient from './apiClient';
import { config } from '../config';
import { authService } from './auth';
import { Voice } from './scriptService';
import { blobToAudioBuffer, convertAudioBufferToMP3, detectSupportedAudioFormat } from '../utils/audioConversion';

const API_URL = config.apiUrl;

export interface MyVoicesResponse {
  voices: Voice[];
  limit: number; // -1 = unlimited, 0 = feature unavailable
  used: number;
}

export interface VoicePreviewResponse {
  sampleAudio: string;   // full URL to the generated sample clip
  audioContent: string;  // base64 mp3 for immediate playback
  outputFormat: string;
  audioRef?: string;     // design previews return the reference clip path (cloned on save)
}

export interface CreateVoicePayload {
  name: string;
  sampleAudio: string;
  allowOthers: boolean;
  // Clone voices:
  audioRef?: string;
  consent?: boolean;
  // Designed voices:
  description?: string;
}

/**
 * Decode any audio Blob/File to an MP3 Blob and report its duration in seconds.
 * Reuses the shared MediaBunny-based conversion pipeline.
 */
export async function toMp3WithDuration(blob: Blob): Promise<{ mp3: Blob; duration: number }> {
  // Ensures the MP3 encoder is registered for browsers without native WebCodec support
  const format = await detectSupportedAudioFormat();
  if (format === 'unsupported') {
    throw new Error('Your browser does not support audio conversion. Please try a different browser.');
  }

  const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext);
  const audioContext: AudioContext = new AudioCtx();
  try {
    const audioBuffer = await blobToAudioBuffer(blob, audioContext);
    const duration = audioBuffer.duration;
    const mp3 = await convertAudioBufferToMP3(audioBuffer);
    return { mp3, duration };
  } finally {
    audioContext.close().catch(() => { /* ignore */ });
  }
}

class VoiceService {
  /** List the current user's own custom voices, with quota info. */
  async getMyVoices(): Promise<MyVoicesResponse> {
    const response = await apiClient.get(`${API_URL}/voices/mine`);
    return response.data;
  }

  /** Upload an MP3 reference clip; returns the stored audio_ref path. */
  async uploadReference(mp3: Blob): Promise<string> {
    const token = authService.getToken();
    const formData = new FormData();
    formData.append('audio', mp3, `voice_ref_${Date.now()}.mp3`);

    const response = await fetch(`${API_URL}/upload-voice-ref`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to upload reference clip');
    }
    const data = await response.json();
    return data.audioRef as string;
  }

  /** Generate a preview clip from an uploaded reference (Qwen3-TTS voice clone). */
  async preview(audioRef: string): Promise<VoicePreviewResponse> {
    const response = await apiClient.post(`${API_URL}/voices/preview`, { audioRef });
    return response.data;
  }

  /** Generate a preview clip from a natural-language description (Qwen3-TTS Voice Design). */
  async designPreview(description: string): Promise<VoicePreviewResponse> {
    const response = await apiClient.post(`${API_URL}/voices/design/preview`, { description });
    return response.data;
  }

  /** Save a new custom voice. */
  async createVoice(payload: CreateVoicePayload): Promise<Voice> {
    const response = await apiClient.post(`${API_URL}/voices`, payload);
    return response.data.voice;
  }

  /** Update a voice's name and/or sharing. */
  async updateVoice(id: string, updates: { name?: string; allowOthers?: boolean }): Promise<Voice> {
    const response = await apiClient.put(`${API_URL}/voices/${id}`, updates);
    return response.data.voice;
  }

  /** Delete one of the user's own custom voices. */
  async deleteVoice(id: string): Promise<void> {
    await apiClient.delete(`${API_URL}/voices/${id}`);
  }

  /** Set (or clear) the user's default voice (voices.id, or null to clear). */
  async setDefaultVoice(voiceId: string | null): Promise<void> {
    await apiClient.put(`${API_URL}/user/default-voice`, { voiceId });
  }
}

export const voiceService = new VoiceService();
